import { useState, useEffect } from 'react';
import axios from 'axios';
import { usePatientPortal } from '../context/PatientPortalContext';
import { calculateDynamicTarget, calculateZScore_V2, calculateWeightedAverageAbsoluteZScore, calculateSystemAndLifestyleDeviations } from '../utils/scoringEngineV2';
import * as math from 'mathjs';
import { analyzeParameterData } from '../utils/parameterDataDebugger';
import { logParameterCountReport } from '../utils/parameterCountValidator';
import { auditParameterConfiguration, PARAMETERS_MD_PARAMETERS } from '../utils/parameterAudit';
// Parameter mapping utilities for display labels
import { getVitalsDisplayLabel, isVitalsParameter } from '../utils/vitalsDisplayMapping';
import { getBiometricsDisplayLabel, isBiometricsParameter } from '../utils/biometricsDisplayMapping';
import { getDisplayLabel as getPhysicalExamDisplayLabel, isPhysicalExamParameter } from '../utils/physicalExamDisplayMapping';
import { getLifestyleDisplayLabel, isLifestyleParameter } from '../utils/lifestyleDisplayMapping';
import { getImagingDisplayLabel, isImagingParameter } from '../utils/imagingDisplayMapping';
import { labKeyToDisplayNameMap } from '../utils/labParameterMappings';
import { getParameterDisplayName } from '../utils/parameterDisplayNames';

// Parameter naming validation based on parameters.md standard
const validateParameterNaming = (paramKey) => {
  const validPrefixes = [
    'vitals_',
    'biometrics_cognition_',
    'biometrics_vision_',
    'biometrics_pulmonary_',
    'biometrics_biomeasurement_',
    'biometrics_flexibility_',
    'biometrics_balance_',
    'biometrics_strength_',
    'biometrics_endurance_',
    'biometrics_power_',
    'pe_integumentary_',
    'pe_lymph_',
    'pe_heent_',
    'pe_neck_',
    'pe_chest_',
    'pe_cardio_',
    'pe_abdomen_',
    'pe_msk_',
    'pe_neuro_',
    'imaging_spleen_',
    'imaging_liver_',
    'imaging_carotid_',
    'imaging_ekg_',
    'imaging_bladder_',
    'imaging_chest_',
    'imaging_heart_',
    'imaging_gallbladder_',
    'imaging_kidney_',
    'labs_cardiac_',
    'labs_cbc_',
    'labs_coagulation_',
    'labs_glucose_',
    'labs_lipid_',
    'labs_electrolytes_',
    'labs_vitamins_',
    'labs_renal_',
    'labs_inflammation_',
    'labs_hepatology_',
    'labs_protein_',
    'labs_thyroid_',
    'labs_urine_',
    'lifestyle_nutrition_',
    'lifestyle_movement_',
    'lifestyle_recovery_',
    'lifestyle_emotion_',
    'lifestyle_environment_'
  ];

  const hasValidPrefix = validPrefixes.some(prefix => paramKey.startsWith(prefix));

  if (!hasValidPrefix) {
    console.warn(`[Parameter Validation] ⚠️ Parameter "${paramKey}" does not follow parameters.md naming convention. Expected format: <category>_<subcategory>_<parameter_name>`);
    return false;
  }

  return true;
};



/**
 * Gets the appropriate display label for any standardized parameter
 * @param {string} parameterKey - Standardized parameter key from parameters.md
 * @returns {string} - Human-readable display label
 */
const getParameterDisplayLabel = (parameterKey) => {
  // PRIORITY 1: Use centralized parameter display name utility (single source of truth)
  try {
    return getParameterDisplayName(parameterKey);
  } catch (error) {
    // Parameter not found in centralized utility, fall back to legacy methods
    console.warn(`[useHealthSnapshotData] Parameter ${parameterKey} not found in centralized display names, using legacy fallback`);
  }

  // PRIORITY 2: Legacy parameter type-specific display label functions (for parameters not yet migrated)
  if (isVitalsParameter(parameterKey)) {
    return getVitalsDisplayLabel(parameterKey);
  }
  if (isBiometricsParameter && isBiometricsParameter(parameterKey)) {
    return getBiometricsDisplayLabel(parameterKey);
  }
  if (isPhysicalExamParameter && isPhysicalExamParameter(parameterKey)) {
    return getPhysicalExamDisplayLabel(parameterKey);
  }
  if (isLifestyleParameter && isLifestyleParameter(parameterKey)) {
    return getLifestyleDisplayLabel(parameterKey);
  }
  if (isImagingParameter && isImagingParameter(parameterKey)) {
    return getImagingDisplayLabel(parameterKey);
  }

  // PRIORITY 3: Lab parameter mapping (legacy)
  if (parameterKey && parameterKey.startsWith('labs_') && labKeyToDisplayNameMap[parameterKey]) {
    return labKeyToDisplayNameMap[parameterKey];
  }

  // PRIORITY 4: Generate readable name from standardized parameter key (last resort)
  if (parameterKey && parameterKey.includes('_')) {
    const parts = parameterKey.split('_');
    if (parts.length >= 3) {
      // For lab parameters, try to use the mapping first
      if (parts[0] === 'labs' && labKeyToDisplayNameMap[parameterKey]) {
        return labKeyToDisplayNameMap[parameterKey];
      }

      // For other standardized keys, create readable name
      const parameter = parts.slice(2).join(' '); // ldl, wbc, etc.

      // Create a more readable name
      const readableName = parameter
        .replace(/_/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase());

      return readableName;
    }
  }

  // Return the parameter key itself (standardized naming)
  return parameterKey;
};

const usePortalHealthScoring = (previewData = null) => {
  // Try to use context, but handle case where it doesn't exist (staff preview mode)
  let patient = null;
  let getPortalAxios = null;

  try {
    const context = usePatientPortal();
    patient = context?.patient;
    getPortalAxios = context?.getPortalAxios;
  } catch (error) {
    // Context doesn't exist - we're in staff preview mode
    if (!previewData) {
      console.warn('usePortalHealthScoring: No context and no preview data');
    }
  }
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [overallDeviation, setOverallDeviation] = useState({ average: null, count: 0, sumOfAbsoluteZScores: 0 });
  const [systemDeviations, setSystemDeviations] = useState({});
  const [lifestyleDeviations, setLifestyleDeviations] = useState({});
  const [coreLifestyleComponents, setCoreLifestyleComponents] = useState({});
  
  const [patientDemographics, setPatientDemographics] = useState(null);
  const [allParamsConfigData, setAllParamsConfigData] = useState(null); 


  useEffect(() => {
    // Use preview data if provided, otherwise use patient from context
    const patientData = previewData?.patient || patient;

    if (!patientData?._id && !previewData) {
      // Don't set error immediately - wait for patientId to be provided
      // This prevents the error from showing during initial portal loading
      setLoading(false);
      setTableData([]);
      setOverallDeviation({ average: null, count: 0, sumOfAbsoluteZScores: 0 });
      setSystemDeviations({});
      setLifestyleDeviations({});
      setCoreLifestyleComponents({});
      setAllParamsConfigData(null);
      setError(null); // Clear any previous errors
      return;
    }

    const fetchDataAndProcess = async () => {
      setLoading(true);
      setError(null);
      setAllParamsConfigData(null); 
      try {
        const [
          targetFormulasRes,
          scoringVitalsRes,
          scoringLabsRes,
          scoringBiometricsRes,
          scoringPhysicalExamRes,
          scoringLifestyleRes,
          scoringImagingRes,
          weightsVitalsRes,
          weightsLabsRes,
          weightsBiometricsRes,
          weightsPhysicalExamRes,
          weightsLifestyleRes,
          weightsImagingRes,
          nutritionParamsRes,
          patientDataRes
        ] = await Promise.all([
          axios.get(`${window.location.origin}/configs/parameter_target_formulas_vn.json`),
          axios.get(`${window.location.origin}/configs/scoring_config_vitals.json`),
          axios.get(`${window.location.origin}/configs/scoring_config_labs.json`),
          axios.get(`${window.location.origin}/configs/scoring_config_biometrics.json`),
          axios.get(`${window.location.origin}/configs/scoring_config_physical_exam.json`),
          axios.get(`${window.location.origin}/configs/scoring_config_lifestyle.json`),
          axios.get(`${window.location.origin}/configs/scoring_config_imaging.json`),
          axios.get(`${window.location.origin}/configs/parameter_weights_vitals.json`),
          axios.get(`${window.location.origin}/configs/parameter_weights_labs.json`),
          axios.get(`${window.location.origin}/configs/parameter_weights_biometrics.json`),
          axios.get(`${window.location.origin}/configs/parameter_weights_physical_exam.json`),
          axios.get(`${window.location.origin}/configs/parameter_weights_lifestyle.json`),
          axios.get(`${window.location.origin}/configs/parameter_weights_imaging.json`),
          axios.get(`${window.location.origin}/configs/nutrition_parameters.json`),
          // Use preview data if available, otherwise fetch from API
          previewData ? Promise.resolve({ data: previewData }) : getPortalAxios().get(`/portal/comprehensive-scoring-data`)
        ]);

        const fetchedTargetFormulasConfig = targetFormulasRes.data;
        const patientApiData = previewData || patientDataRes.data.data;

        console.log('Debug patientApiData structure:', patientApiData);
        console.log('Debug patientApiData keys:', Object.keys(patientApiData || {}));
        if (patientApiData?.patient) {
          console.log('Debug patientApiData.patient keys:', Object.keys(patientApiData.patient));
        }

        // Handle different data structures for preview vs regular mode
        let demographics = null;
        if (previewData) {
          // In preview mode, combine patient basic info with demographics
          const patientInfo = patientApiData.patient || {};
          const demographicsInfo = patientApiData.patient?.demographics || {};

          // Merge patient info and demographics
          demographics = {
            ...patientInfo,
            ...demographicsInfo
          };

          console.log('Debug demographics found:', demographics);
          console.log('Debug demographics keys:', demographics ? Object.keys(demographics) : 'null');
        } else {
          demographics = patientApiData.demographics;
        }

        if (!patientApiData || !demographics) {
          console.error('Debug: Missing demographics. patientApiData:', !!patientApiData, 'demographics:', !!demographics);
          throw new Error("Fetched patient data is missing demographics or is in an unexpected format.");
        }

        setPatientDemographics(demographics);

        // Enhanced parameter data debugging - optimized for performance
        console.log('[useHealthSnapshotData] 🔍 Starting parameter processing and validation...');

        // Batch analysis of available patient data
        const availableDataKeys = Object.keys(patientApiData);
        const dataByCategory = {
          labs: availableDataKeys.filter(key => key.startsWith('labs_')).length,
          vitals: availableDataKeys.filter(key => key.startsWith('vitals_')).length,
          biometrics: availableDataKeys.filter(key => key.startsWith('biometrics_')).length,
          pe: availableDataKeys.filter(key => key.startsWith('pe_')).length,
          lifestyle: availableDataKeys.filter(key => key.startsWith('lifestyle_')).length,
          imaging: availableDataKeys.filter(key => key.startsWith('imaging_')).length
        };

        console.log('[useHealthSnapshotData] 📊 Patient data summary:', {
          totalDataPoints: availableDataKeys.length,
          byCategory: dataByCategory
        });

        const allRawScoringParams = [
          ...((scoringVitalsRes.data && scoringVitalsRes.data.parameters) || []),
          ...((scoringLabsRes.data && scoringLabsRes.data.parameters) || []),
          ...((scoringBiometricsRes.data && scoringBiometricsRes.data.parameters) || []),
          ...((scoringPhysicalExamRes.data && scoringPhysicalExamRes.data.parameters) || []),
          ...((scoringLifestyleRes.data && scoringLifestyleRes.data.parameters) || []),
          ...((scoringImagingRes.data && scoringImagingRes.data.parameters && Array.isArray(scoringImagingRes.data.parameters)) ? scoringImagingRes.data.parameters : []),
        ];

        const uniqueScoringParamsMap = new Map();
        allRawScoringParams.forEach(param => {
          if (param && param.key && !uniqueScoringParamsMap.has(param.key)) {
            uniqueScoringParamsMap.set(param.key, param);
          }
        });
        const dedupedScoringParameters = Array.from(uniqueScoringParamsMap.values());
        const fetchedScoringConfig = { parameters: dedupedScoringParameters };

        const mergedParameterWeightsData = {
          parameters: {
            ...((weightsVitalsRes.data && weightsVitalsRes.data.parameters) || {}),
            ...((weightsLabsRes.data && weightsLabsRes.data.parameters) || {}),
            ...((weightsBiometricsRes.data && weightsBiometricsRes.data.parameters) || {}),
            ...((weightsPhysicalExamRes.data && weightsPhysicalExamRes.data.parameters) || {}),
            ...((weightsLifestyleRes.data && weightsLifestyleRes.data.parameters) || {}),
            ...((weightsImagingRes.data && weightsImagingRes.data.parameters) || {}),
          }
        };

        if (!fetchedTargetFormulasConfig || !fetchedScoringConfig || !fetchedScoringConfig.parameters || !mergedParameterWeightsData || !mergedParameterWeightsData.parameters) {
          throw new Error("Fetched configuration data is incomplete.");
        }

        // CRITICAL: Audit parameter configuration completeness
        const configuredParameterKeys = fetchedScoringConfig.parameters.map(p => p.key);
        const configAudit = auditParameterConfiguration(configuredParameterKeys);

        console.log('\n' + '='.repeat(80));
        console.log('🔍 CRITICAL PARAMETER CONFIGURATION AUDIT');
        console.log('='.repeat(80));
        console.log(`📊 Expected parameters (parameters.md): ${configAudit.totalExpected}`);
        console.log(`📊 Configured parameters (scoring configs): ${configAudit.totalConfigured}`);
        console.log(`📊 Missing from configuration: ${configAudit.missingCount}`);
        console.log(`📊 Extra in configuration: ${configAudit.extraCount}`);
        console.log(`📊 Configuration complete: ${configAudit.configurationComplete ? '✅ YES' : '❌ NO'}`);

        if (configAudit.missingCount > 0) {
          console.error('❌ CRITICAL: Missing parameters from configuration files:');
          Object.entries(configAudit.missingByCategory).forEach(([category, subcategories]) => {
            Object.entries(subcategories).forEach(([subcategory, params]) => {
              console.error(`   ${category}_${subcategory}: ${params.length} missing (${params.slice(0, 3).join(', ')}${params.length > 3 ? '...' : ''})`);
            });
          });
          console.error('❌ This will result in incomplete health scoring and must be fixed!');
        }

        if (configAudit.extraCount > 0) {
          console.warn('⚠️ Extra parameters in configuration (not in parameters.md):');
          configAudit.extraInConfig.slice(0, 10).forEach(param => console.warn(`   ${param}`));
          if (configAudit.extraCount > 10) console.warn(`   ... and ${configAudit.extraCount - 10} more`);
        }

        console.log('='.repeat(80));

        setAllParamsConfigData(mergedParameterWeightsData.parameters);

        const allScorableParams = [...fetchedScoringConfig.parameters];

        if (configAudit.missingCount > 0) {
          console.error(`❌ CONFIGURATION ERROR: ${configAudit.missingCount} parameters from parameters.md are missing configuration entries.`);
          console.error('🚫 STRICT MODE: No synthetic parameters will be generated. All parameters must have proper configuration.');
        }

        console.log(`🔧 Total parameters for processing: ${allScorableParams.length}`);

        const processedParamsMap = new Map();

        const derivedParamsConfig = {
          'vitals_map': {
            dependencies: ['vitals_sbp', 'vitals_dbp'],
            calculate: (idealSbp, idealDbp) => idealDbp + (idealSbp - idealDbp) / 3,
            formulaString: (iSbp, iDbp, iMap) => `Ideal DBP + 1/3 * (Ideal SBP - Ideal DBP)\n${iDbp?.toFixed(2)} + 1/3 * (${iSbp?.toFixed(2)} - ${iDbp?.toFixed(2)}) = ${iMap?.toFixed(2)}`
          },
          'vitals_pp': {
            dependencies: ['vitals_sbp', 'vitals_dbp'],
            calculate: (idealSbp, idealDbp) => idealSbp - idealDbp,
            formulaString: (iSbp, iDbp, iPp) => `Ideal SBP - Ideal DBP\n${iSbp?.toFixed(2)} - ${iDbp?.toFixed(2)} = ${iPp?.toFixed(2)}`
          },
          'vitals_weight': {
            dependencies: ['vitals_bmi', 'vitals_height'],
            calculate: (idealBmi, actualHeightCm) => {
                if (actualHeightCm && actualHeightCm > 0 && idealBmi && typeof idealBmi === 'number') {
                    const heightM = actualHeightCm / 100;
                    return idealBmi * (heightM * heightM);
                }
                return null;
            },
            formulaString: (iBmi, aHcm, iWeight) => `Ideal BMI * (Actual Height_m)^2\n${iBmi?.toFixed(1)} kg/m² * (${(aHcm / 100)?.toFixed(2)} m)^2 = ${iWeight?.toFixed(2)} kg`
          },
          'labs_cbc_nlr': {
            dependencies: ['labs_cbc_neut', 'labs_cbc_lymph'], 
            calculate: (idealNeut, idealLymph) => {
                if (typeof idealNeut === 'number' && typeof idealLymph === 'number' && idealLymph !== 0) {
                    return idealNeut / idealLymph;
                }
                return null;
            },
            formulaString: (iNeut, iLymph, iNLR) => `Ideal NEUT / Ideal LYMPH\n${iNeut?.toFixed(2)} / ${iLymph?.toFixed(2)} = ${iNLR?.toFixed(2)}`
          },
          'labs_lipid_vldl': {
            dependencies: ['labs_lipid_trig'], 
            calculate: (idealTrig) => {
                if (typeof idealTrig === 'number') {
                    return idealTrig / 5;
                }
                return null;
            },
            formulaString: (iTrig, iVLDL) => `Ideal Triglycerides / 5\n${iTrig?.toFixed(2)} / 5 = ${iVLDL?.toFixed(2)} mg/dL`
          },
          'labs_protein_alb_glob_ratio': {
            dependencies: ['labs_protein_albumin', 'labs_protein_total'],
            calculate: (idealAlbumin, idealTotalProtein) => {
                if (typeof idealAlbumin === 'number' && typeof idealTotalProtein === 'number') {
                    const globulin = idealTotalProtein - idealAlbumin;
                    if (globulin > 0) {
                        return idealAlbumin / globulin;
                    }
                }
                return null;
            },
            formulaString: (iAlb, iTp, iRatio) => `Ideal Albumin / (Ideal Total Protein - Ideal Albumin)\n${iAlb?.toFixed(2)} / (${iTp?.toFixed(2)} - ${iAlb?.toFixed(2)}) = ${iRatio?.toFixed(2)}`
          },
          'biometrics_balance_ybt_performance_ratio': {
            dependencies: ['biometrics_balance_ybt_right_composite_reach', 'biometrics_balance_ybt_left_composite_reach'],
            calculate: (rightComposite, leftComposite) => {
                if (typeof rightComposite === 'number' && typeof leftComposite === 'number' && rightComposite > 0 && leftComposite > 0) {
                    const asymmetry = Math.abs(rightComposite - leftComposite);
                    const average = (rightComposite + leftComposite) / 2;
                    return (asymmetry / average) * 100;
                }
                return null;
            },
            formulaString: (iRight, iLeft, iRatio) => `|Right Composite - Left Composite| / ((Right + Left) / 2) × 100\n|${iRight?.toFixed(2)} - ${iLeft?.toFixed(2)}| / ((${iRight?.toFixed(2)} + ${iLeft?.toFixed(2)}) / 2) × 100 = ${iRatio?.toFixed(2)}%`
          }
        };
        
        const baseParamKeysForIdealTargetCalc = allScorableParams
            .map(p => p.key)
            .filter(k => !derivedParamsConfig[k] || k === 'vitals_bmi' || k === 'labs_cbc_neut' || k === 'labs_cbc_lymph' || k === 'labs_lipid_trig');
        
        const derivedParamKeysForIdealTargetCalc = Object.keys(derivedParamsConfig)
            .filter(k => allScorableParams.some(p => p.key === k));


        for (const paramKey of baseParamKeysForIdealTargetCalc) {
          if (paramKey === 'height') continue; 

          const paramDetails = {
            key: paramKey,
            parameterName: getParameterDisplayLabel(paramKey) || allScorableParams.find(p => p.key === paramKey)?.name || paramKey,
            currentValue: null, idealTarget: null, targetFormulaUsed: 'N/A', targetFormulaDetails: '', error: null,
          };
          const patientParamObject = patientApiData[paramKey];
          const patientParamValue = patientParamObject?.mostRecent?.value;
          const paramScoringConfigEntry = allScorableParams.find(p => p.key === paramKey);

          if (!paramScoringConfigEntry) {
            paramDetails.error = "Internal Error: Scoring config missing in master list";
            processedParamsMap.set(paramKey, paramDetails); continue;
          }
          
          if (paramScoringConfigEntry.target_source === "dynamic_formula") {
            const formulaKeyToUse = paramScoringConfigEntry.target_coefficients_key || paramKey;
            const targetFormulaInfo = fetchedTargetFormulasConfig.find(f => f.key === formulaKeyToUse);
            if (targetFormulaInfo) {
              const idealTargetResult = calculateDynamicTarget(formulaKeyToUse, patientApiData.demographics, fetchedTargetFormulasConfig);
              if (idealTargetResult && typeof idealTargetResult.value === 'number' && !isNaN(idealTargetResult.value)) {
                paramDetails.idealTarget = idealTargetResult.value;
                paramDetails.targetFormulaUsed = targetFormulaInfo.formula_type === "fixed_ideal" ? "No (Static)" : "Yes (Dynamic)";
                // Pass the full details for UI rendering
                paramDetails.targetCalculationSteps = idealTargetResult.calculationDetails;
                paramDetails.targetFinalFormula = idealTargetResult.finalFormula;
                // Keep a simplified version for other potential uses, or remove if unused elsewhere
                paramDetails.targetFormulaDetails = `Formula: ${formulaKeyToUse}, Value: ${idealTargetResult.value.toFixed(2)}`;
              } else {
                paramDetails.error = idealTargetResult?.error?.error || `Dynamic target calc failed for ${formulaKeyToUse}`;
              }
            } else {
              paramDetails.error = `Dynamic formula definition not found for key ${formulaKeyToUse}`;
            }
            } else if (paramScoringConfigEntry.target_source === "fixed_ideal" && paramScoringConfigEntry.target_value !== undefined && paramScoringConfigEntry.target_value !== null) {
              paramDetails.idealTarget = parseFloat(paramScoringConfigEntry.target_value);
              if (isNaN(paramDetails.idealTarget)) {
                paramDetails.error = `Invalid target_value in scoring config for ${paramKey}`;
              } else {
                paramDetails.targetFormulaUsed = "No (Static from Scoring Config)";
                paramDetails.targetFormulaDetails = `Static ideal: ${paramDetails.idealTarget.toFixed(2)}`;
              }
            } else if (paramScoringConfigEntry.target_source === "none" || (paramScoringConfigEntry.target_source === "fixed_ideal" && paramScoringConfigEntry.target_value === null) || paramScoringConfigEntry.target_source === "calculated_by_engine") {
              paramDetails.idealTarget = null;
              paramDetails.targetFormulaUsed = "N/A (Informational/Engine Calculated)";
              paramDetails.targetFormulaDetails = `Target source: ${paramScoringConfigEntry.target_source}`;
              paramDetails.error = null; 
            } else if (paramScoringConfigEntry.target_source === "derived_dynamic") {
               if (!derivedParamsConfig[paramKey]) { 
                  paramDetails.error = `Target for ${paramKey} (source: derived_dynamic) needs specific ideal target derivation logic or config.`;
               } 
            } else { 
              paramDetails.error = `Missing or unhandled target definition for ${paramKey}`; 
            }
            processedParamsMap.set(paramKey, paramDetails);
        }

        for (const paramKey of derivedParamKeysForIdealTargetCalc) {
          let paramDetails = processedParamsMap.get(paramKey) || {
            key: paramKey,
            parameterName: getParameterDisplayLabel(paramKey) || allScorableParams.find(p => p.key === paramKey)?.name || paramKey,
            currentValue: null, idealTarget: null, targetFormulaUsed: 'N/A', targetFormulaDetails: '', error: null,
          };
          const paramScoringConfigEntry = allScorableParams.find(p => p.key === paramKey);

          if (paramScoringConfigEntry?.target_source === 'derived_dynamic' && derivedParamsConfig[paramKey]) {
            const config = derivedParamsConfig[paramKey];
            let depValues = [];
            let depErrors = [];

            if (paramKey === 'vitals_weight') { 
                const idealBmiDetail = processedParamsMap.get('vitals_bmi'); 
                const heightVitalData = patientApiData['vitals_height']; 
                const actualHeightCmValue = (heightVitalData && heightVitalData.mostRecent && heightVitalData.mostRecent.value !== undefined && heightVitalData.mostRecent.value !== null)
                                            ? parseFloat(heightVitalData.mostRecent.value)
                                            : undefined;

                if (!idealBmiDetail || idealBmiDetail.idealTarget === null || isNaN(idealBmiDetail.idealTarget)) {
                    depErrors.push(`Ideal BMI: ${idealBmiDetail?.error || 'missing/invalid'}`);
                } else { depValues.push(idealBmiDetail.idealTarget); }

                if (actualHeightCmValue === undefined || isNaN(actualHeightCmValue) || actualHeightCmValue <= 0) {
                    depErrors.push(`Actual Patient Height (from Vitals): missing or invalid`);
                } else { depValues.push(actualHeightCmValue); }
            } else { 
                config.dependencies.forEach(depKey => {
                    const depDetail = processedParamsMap.get(depKey);
                    if (!depDetail || depDetail.idealTarget === null || isNaN(depDetail.idealTarget)) {
                        depErrors.push(`Ideal ${depKey.toUpperCase()}: ${depDetail?.error || 'missing/invalid'}`);
                    } else { depValues.push(depDetail.idealTarget); }
                });
            }

            if (depErrors.length > 0) {
                paramDetails.error = paramDetails.error || `Cannot calculate Ideal ${paramKey.toUpperCase()}: ${depErrors.join('; ')}.`;
            } else if (depValues.length === config.dependencies.length && depValues.every(val => typeof val === 'number' && !isNaN(val))) {
                paramDetails.idealTarget = config.calculate(...depValues);
                if (paramDetails.idealTarget === null || isNaN(paramDetails.idealTarget)) {
                     paramDetails.error = paramDetails.error || `Ideal ${paramKey.toUpperCase()} calculation resulted in null/NaN.`;
                } else {
                    paramDetails.targetFormulaUsed = "Yes (Derived Ideal)";
                    paramDetails.targetFormulaDetails = `Derived Ideal ${paramKey.toUpperCase()}:\n${config.formulaString(...depValues, paramDetails.idealTarget)}`;
                }
            } else { 
                paramDetails.error = paramDetails.error || `Dependency ideal target(s) missing for ${paramKey.toUpperCase()} derived ideal.`;
            }
          }
          processedParamsMap.set(paramKey, paramDetails);
        }
        
        const finalTableDataOutput = [];
        let invalidParameterNames = [];
        let syntheticWeightCount = 0;
        let dataExtractionStats = {
          mostRecent: 0,
          historicalData: 0,
          results: 0,
          directValue: 0,
          nestedValue: 0,
          noData: 0,
          dataStructureIssues: 0
        };

        for (const scorableParamConfig of allScorableParams) {
          const paramKey = scorableParamConfig.key;

          // Validate parameter naming against parameters.md standard (batch validation)
          const isValidParameterName = validateParameterNaming(paramKey);
          if (!isValidParameterName) {
            // Collect invalid parameters for batch reporting instead of individual logs
            if (!invalidParameterNames) invalidParameterNames = [];
            invalidParameterNames.push(paramKey);
          }

          let details = processedParamsMap.get(paramKey) || {
              key: paramKey,
              parameterName: getParameterDisplayLabel(paramKey) || scorableParamConfig.name || paramKey,
              currentValue: null, idealTarget: null, targetFormulaUsed: 'N/A', targetFormulaDetails: '', error: null,
              overall_significance_weight: null,
          };

          if (!details.individual_weights) {
            details.individual_weights = { system_weights: {}, lifestyle_weights: {} };
          }
          if (details.overall_significance_weight === undefined) {
            details.overall_significance_weight = null;
          }
          if (!details.category) {
            details.category = 'Uncategorized'; 
          }

          let weightInfo = mergedParameterWeightsData.parameters[paramKey];

          // For synthetic parameters (missing from config), create default weight configuration
          if (!weightInfo && details._synthetic) {
            const category = paramKey.split('_')[0];
            weightInfo = {
              displayName: paramKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
              category: category.charAt(0).toUpperCase() + category.slice(1),
              overall_clinical_significance_weight: 50, // Default weight
              systems: {
                // Default system weights based on parameter category
                ...(category === 'labs' && {
                  immune: { S: 20, F: 20, R: 20 },
                  metabolic: { S: 15, F: 15, R: 15 }
                }),
                ...(category === 'vitals' && {
                  cardiovascular: { S: 25, F: 25, R: 25 },
                  respiratory: { S: 20, F: 20, R: 20 }
                }),
                ...(category === 'biometrics' && {
                  musculoskeletal: { S: 20, F: 20, R: 20 },
                  neurological: { S: 15, F: 15, R: 15 }
                }),
                ...(category === 'lifestyle' && {
                  metabolic: { S: 10, F: 10, R: 10 }
                })
              },
              lifestyle_domains: [
                { domain: "nutrition", weight: 10, is_core_component: false },
                { domain: "movement", weight: 10, is_core_component: false },
                { domain: "recovery", weight: 5, is_core_component: false },
                { domain: "mental_health", weight: 5, is_core_component: false }
              ],
              _synthetic: true
            };
            syntheticWeightCount++;
          }

          if (weightInfo && weightInfo.category) {
            details.category = weightInfo.category;
          } else {
            const scoringConfigForParam = allScorableParams.find(p => p.key === paramKey);
            if (scoringConfigForParam && scoringConfigForParam.category) {
              details.category = scoringConfigForParam.category;
            }
            if (!details.category || details.category === 'Uncategorized') {
              details.category = 'Uncategorized';
            }
          }
          
          if (weightInfo && weightInfo.overall_clinical_significance_weight !== undefined) {
            details.overall_significance_weight = weightInfo.overall_clinical_significance_weight;
          } else if (weightInfo && weightInfo.final_weight !== undefined) { 
            details.overall_significance_weight = weightInfo.final_weight; 
          }

          if (details.overall_significance_weight === null) {
            const upperParamKey = paramKey.toUpperCase();
            if (upperParamKey === 'VITALS_HEIGHT' || upperParamKey === 'HEIGHTCM' || upperParamKey === 'HEIGHT') { 
              details.overall_significance_weight = 0;
            } else if (weightInfo && weightInfo.systems) {
              let allSystemComponentWeights = [];
              const sfrKeyMap = { structure: 'S', function: 'F', risk: 'R', s: 'S', f: 'F', r: 'R' };

              for (const systemName in weightInfo.systems) {
                const systemGroup = weightInfo.systems[systemName]; 
                if (systemGroup && typeof systemGroup === 'object') {
                  for (const componentKey in systemGroup) { 
                    const componentValue = systemGroup[componentKey];
                    if (componentValue && typeof componentValue === 'object' && typeof componentValue.final_weight === 'number') {
                      allSystemComponentWeights.push(componentValue.final_weight);
                    } else if (typeof componentValue === 'number') { 
                      allSystemComponentWeights.push(componentValue);
                    }
                  }
                }
              }

              if (allSystemComponentWeights.length > 0) {
                details.overall_significance_weight = Math.max(...allSystemComponentWeights);
              }
            }
          }

          if (weightInfo) {
            const sfrKeyMap = { structure: 'S', function: 'F', risk: 'R', s: 'S', f: 'F', r: 'R' };
            if (weightInfo.systems) {
              for (const systemName in weightInfo.systems) {
                const systemGroup = weightInfo.systems[systemName];
                if (systemGroup && typeof systemGroup === 'object') {
                  let currentSystemWeights = {};
                  for (const componentKey in systemGroup) { 
                    const normalizedSFRKey = sfrKeyMap[componentKey.toLowerCase()] || componentKey.toUpperCase(); 
                    const componentValue = systemGroup[componentKey];
                    
                    let weightToStore;
                    if (componentValue && typeof componentValue === 'object' && typeof componentValue.final_weight === 'number') {
                      weightToStore = componentValue.final_weight;
                    } else if (typeof componentValue === 'number') {
                      weightToStore = componentValue;
                    }

                    if (typeof weightToStore === 'number' && ['S', 'F', 'R'].includes(normalizedSFRKey)) {
                       currentSystemWeights[normalizedSFRKey] = weightToStore;
                    }
                  }
                  if (Object.keys(currentSystemWeights).length > 0) {
                    details.individual_weights.system_weights[systemName] = currentSystemWeights;
                  }
                }
              }
            }
            if (weightInfo.lifestyle_domains) {
              for (const domainName in weightInfo.lifestyle_domains) {
                const domainGroup = weightInfo.lifestyle_domains[domainName];
                if (domainGroup && typeof domainGroup.final_weight === 'number') {
                  details.individual_weights.lifestyle_weights[domainName] = domainGroup.final_weight;
                }
              }
            }
          }
          
          let patientParamObject = patientApiData[paramKey];
          let actualBackendKey = paramKey;

          details.backendKey = actualBackendKey;

          // Enhanced data retrieval logic to ensure complete historical data access
          let extractedValue = null;
          let dataRetrievalMethod = null;
          let dataRetrievalDetails = {};

          // Optimized parameter debugging - collect stats instead of individual logs
          const debugParameterRetrieval = (paramKey, patientParamObject) => {
            if (!patientParamObject) {
              return {
                hasPatientData: false,
                availableKeys: 'N/A',
                dataStructure: null
              };
            }

            const structure = {
              hasPatientData: true,
              availableKeys: Object.keys(patientParamObject),
              hasMostRecent: !!patientParamObject.mostRecent,
              mostRecentValue: patientParamObject.mostRecent?.value,
              mostRecentDate: patientParamObject.mostRecent?.date || patientParamObject.mostRecent?.timestamp,
              hasData: !!patientParamObject.data,
              dataLength: patientParamObject.data?.length,
              hasDirectValue: patientParamObject.value !== undefined,
              directValue: patientParamObject.value,
              hasResults: !!patientParamObject.results,
              resultsLength: patientParamObject.results?.length,
              objectStructure: Object.keys(patientParamObject).reduce((acc, key) => {
                acc[key] = typeof patientParamObject[key];
                return acc;
              }, {})
            };

            return structure;
          };

          const debugInfo = debugParameterRetrieval(paramKey, patientParamObject);
          dataRetrievalDetails.debugInfo = debugInfo;

          if (patientParamObject) {
            // Strategy 1: Try mostRecent value (preferred method)
            if (patientParamObject.mostRecent &&
                patientParamObject.mostRecent.value !== undefined &&
                patientParamObject.mostRecent.value !== null) {
              extractedValue = patientParamObject.mostRecent.value;
              dataRetrievalMethod = 'mostRecent';
              dataRetrievalDetails.source = 'mostRecent';
              dataRetrievalDetails.timestamp = patientParamObject.mostRecent.date || patientParamObject.mostRecent.timestamp;
              dataExtractionStats.mostRecent++;
            }
            // Strategy 2: Try historical data array (fallback method)
            else if (patientParamObject.data && Array.isArray(patientParamObject.data) && patientParamObject.data.length > 0) {
              // Sort by multiple possible timestamp fields to get the most recent entry
              const sortedData = patientParamObject.data.sort((a, b) => {
                const aTime = new Date(a.timestamp || a.date || a.entryDate || a.resultDate || 0);
                const bTime = new Date(b.timestamp || b.date || b.entryDate || b.resultDate || 0);
                return bTime - aTime;
              });
              const latestEntry = sortedData[0];
              if (latestEntry && latestEntry.value !== undefined && latestEntry.value !== null) {
                extractedValue = latestEntry.value;
                dataRetrievalMethod = 'historicalData';
                dataRetrievalDetails.source = 'data array';
                dataRetrievalDetails.timestamp = latestEntry.timestamp || latestEntry.date || latestEntry.entryDate || latestEntry.resultDate;
                dataRetrievalDetails.totalHistoricalEntries = patientParamObject.data.length;
                dataExtractionStats.historicalData++;
              }
            }
            // Strategy 3: Try results array (for lab/imaging data)
            else if (patientParamObject.results && Array.isArray(patientParamObject.results) && patientParamObject.results.length > 0) {
              const sortedResults = patientParamObject.results.sort((a, b) => {
                const aTime = new Date(a.resultDate || a.timestamp || a.date || 0);
                const bTime = new Date(b.resultDate || b.timestamp || b.date || 0);
                return bTime - aTime;
              });
              const latestResult = sortedResults[0];
              if (latestResult && latestResult.value !== undefined && latestResult.value !== null) {
                extractedValue = latestResult.value;
                dataRetrievalMethod = 'results';
                dataRetrievalDetails.source = 'results array';
                dataRetrievalDetails.timestamp = latestResult.resultDate || latestResult.timestamp || latestResult.date;
                dataRetrievalDetails.totalResults = patientParamObject.results.length;
                dataExtractionStats.results++;
              }
            }
            // Strategy 4: Try direct value property (last resort)
            else if (patientParamObject.value !== undefined && patientParamObject.value !== null) {
              extractedValue = patientParamObject.value;
              dataRetrievalMethod = 'directValue';
              dataRetrievalDetails.source = 'direct value';
              dataExtractionStats.directValue++;
            }
            // Strategy 5: Check for nested value structures
            else {
              // Look for nested value structures in the object
              const checkNestedValue = (obj, path = []) => {
                if (typeof obj !== 'object' || obj === null) return null;

                // Check common value property names
                const valueProps = ['value', 'result', 'measurement', 'reading'];
                for (const prop of valueProps) {
                  if (obj[prop] !== undefined && obj[prop] !== null) {
                    return { value: obj[prop], path: [...path, prop] };
                  }
                }

                // Recursively check nested objects (limit depth to prevent infinite loops)
                if (path.length < 3) {
                  for (const [key, value] of Object.entries(obj)) {
                    if (typeof value === 'object' && value !== null) {
                      const nested = checkNestedValue(value, [...path, key]);
                      if (nested) return nested;
                    }
                  }
                }

                return null;
              };

              const nestedValue = checkNestedValue(patientParamObject);
              if (nestedValue) {
                extractedValue = nestedValue.value;
                dataRetrievalMethod = 'nestedValue';
                dataRetrievalDetails.source = `nested: ${nestedValue.path.join('.')}`;
                dataExtractionStats.nestedValue++;
              }
            }

            // Track extraction results for batch reporting
            if (extractedValue === null || extractedValue === undefined) {
              if (patientParamObject && Object.keys(patientParamObject).length > 0) {
                dataExtractionStats.dataStructureIssues++;
                dataRetrievalDetails.error = 'No extractable value found in patient data structure';
              } else {
                dataExtractionStats.noData++;
                dataRetrievalDetails.error = 'No patient data object available';
              }
            }
          } else {
            dataExtractionStats.noData++;
            dataRetrievalDetails.error = 'No patient data object available';
          }

          // Store retrieval details for debugging
          details.dataRetrievalMethod = dataRetrievalMethod;
          details.dataRetrievalDetails = dataRetrievalDetails;

          // Handle nested value objects
          if (typeof extractedValue === 'object' && extractedValue !== null && extractedValue.hasOwnProperty('value')) {
            extractedValue = extractedValue.value;
          }

          if (scorableParamConfig.value_mapping) {
            if (typeof extractedValue === 'string') {
              const mappedValue = scorableParamConfig.value_mapping[extractedValue];
              if (mappedValue !== undefined) {
                extractedValue = mappedValue;
              } else {
                details.error = details.error || `Invalid categorical value: "${extractedValue}" for ${paramKey}`;
              }
            } else if (extractedValue !== undefined && extractedValue !== null) {
              details.error = details.error || `Invalid categorical value type for ${paramKey}: ${JSON.stringify(extractedValue)}`;
            }
          }

          if (extractedValue === undefined || extractedValue === null) {
            const isCategoricalError = details.error && (
              details.error.startsWith("Invalid categorical value") ||
              details.error.startsWith("Invalid categorical value type")
            );
            if (paramKey !== 'vitals_height' && !isCategoricalError) { 
                details.error = details.error || "Awaiting data";
            }
          } else if (!details.error) {
            const parsedCurrentValue = parseFloat(extractedValue);

            if (isNaN(parsedCurrentValue)) {
                if (typeof extractedValue === 'string' && !details.error) { 
                    details.error = details.error || `Invalid current value: "${extractedValue}"`;
                } else if (typeof extractedValue !== 'number' && !details.error) { 
                    details.error = details.error || `Current value is not a processable number: ${JSON.stringify(extractedValue)}`;
                }
                if (typeof extractedValue === 'number' && isNaN(extractedValue) && !details.error) {
                    details.error = details.error || `Current value is NaN for ${paramKey}`;
                }
                if (!isNaN(parsedCurrentValue)) { 
                     details.currentValue = parsedCurrentValue;
                } else if (!details.error) { 
                    details.currentValue = extractedValue; 
                }

            } else { 
                details.currentValue = parsedCurrentValue;
            }
          }
          
          if (paramKey === 'vitals_height') { 
            details.idealTarget = null;
            details.targetFormulaUsed = "N/A (Informational)";
            details.targetFormulaDetails = "Patient's measured height. Not scored for Z-score deviation.";
            details.sigmaLow = null; details.sigmaHigh = null; details.selectedSigma = null;
            details.diffFromTarget = null; details.zScore = null; details.absoluteZScore = null;
            if (details.error !== "Awaiting data") details.error = null;
          } else if (!details.error && details.idealTarget !== null && !isNaN(details.idealTarget) && details.currentValue !== null && !isNaN(details.currentValue)) {
            details.sigmaLow = scorableParamConfig.stdDeviation_low !== undefined ? parseFloat(scorableParamConfig.stdDeviation_low) :
                               (scorableParamConfig.stdDeviation !== undefined ? parseFloat(scorableParamConfig.stdDeviation) :
                               (scorableParamConfig.sd?.value !== undefined ? parseFloat(scorableParamConfig.sd.value) : null));
            details.sigmaHigh = scorableParamConfig.stdDeviation_high !== undefined ? parseFloat(scorableParamConfig.stdDeviation_high) :
                                (scorableParamConfig.stdDeviation !== undefined ? parseFloat(scorableParamConfig.stdDeviation) :
                                (scorableParamConfig.sd?.value !== undefined ? parseFloat(scorableParamConfig.sd.value) : null));

            const zScoreResult = calculateZScore_V2(details.currentValue, details.idealTarget, scorableParamConfig);
            if (zScoreResult.error) {
              details.error = details.error || zScoreResult.error;
            } else {
              details.selectedSigma = zScoreResult.selectedSigma;
              details.zScore = zScoreResult.zScore;
              details.diffFromTarget = details.currentValue - details.idealTarget;

              // Enhanced calculation transparency - add detailed formula information
              details.selectedSigmaReason = (zScoreResult.selectedSigmaReason || zScoreResult.reason) ||
                `Selected σ = ${details.selectedSigma?.toFixed(2)} based on ${details.diffFromTarget >= 0 ? 'high' : 'low'} direction`;

              // Generate detailed Z-score formula with actual values
              if (typeof details.zScore === 'number' && !isNaN(details.zScore)) {
                details.absoluteZScore = math.abs(details.zScore);

                details.zScoreFormula = `Z-Score = (Current Value - Ideal Target) / Standard Deviation
Z-Score = (${details.currentValue.toFixed(2)} - ${details.idealTarget.toFixed(2)}) / ${details.selectedSigma.toFixed(2)}
Z-Score = ${details.diffFromTarget.toFixed(2)} / ${details.selectedSigma.toFixed(2)} = ${details.zScore.toFixed(3)}σ

Absolute Z-Score = |${details.zScore.toFixed(3)}| = ${details.absoluteZScore.toFixed(3)}σ`;
              }
            }
          }

          if (typeof details.absoluteZScore === 'number' && typeof details.overall_significance_weight === 'number' && details.overall_significance_weight !== null && !isNaN(details.overall_significance_weight)) {
            details.weightedAbsoluteZScore = details.absoluteZScore * (details.overall_significance_weight / 100);
          } else {
            details.weightedAbsoluteZScore = null; 
          }

          finalTableDataOutput.push(details);
        }
        finalTableDataOutput.forEach(details => {
          const upperParamKey = details.key ? details.key.toUpperCase() : '';

          if (details.overall_significance_weight === null) {
            switch (upperParamKey) {
              case 'VITALS_MAP':
              case 'VITALS_PP': {
                const sbpDetails = finalTableDataOutput.find(p => p.key?.toUpperCase() === 'VITALS_SBP');
                const dbpDetails = finalTableDataOutput.find(p => p.key?.toUpperCase() === 'VITALS_DBP');
                const sbpWeight = sbpDetails?.overall_significance_weight;
                const dbpWeight = dbpDetails?.overall_significance_weight;
                if (typeof sbpWeight === 'number' && typeof dbpWeight === 'number') {
                  details.overall_significance_weight = Math.round((sbpWeight + dbpWeight) / 2);
                }
                break;
              }
              case 'VITALS_HRV':
              case 'HRV':
                details.overall_significance_weight = 85;
                break;
              case 'VITALS_VO2MAX':
              case 'VO2_MAX':
              case 'VO2MAX':
                details.overall_significance_weight = 90;
                break;
              case 'VITALS_SIRS':
              case 'SIRS_SCORE':
              case 'SIRS':
                details.overall_significance_weight = 80;
                break;
              case 'VITALS_PAIN':
              case 'PAIN_LEVEL':
              case 'PAIN':
                details.overall_significance_weight = 75;
                break;
              case 'LABS_LIPID_VLDL': {
                const trigDetails = finalTableDataOutput.find(p => p.key === 'labs_lipid_trig');
                if (typeof trigDetails?.overall_significance_weight === 'number') {
                  details.overall_significance_weight = trigDetails.overall_significance_weight;
                }
                break;
              }
              case 'LABS_LIPID_TC_HDL_RATIO':
              case 'LABS_LIPID_ATHERO_IDX':
              case 'LABS_LIPID_TC_GLU_RATIO': {
                const totalChol = finalTableDataOutput.find(p => p.key === 'labs_lipid_chol_total');
                if (typeof totalChol?.overall_significance_weight === 'number') {
                  details.overall_significance_weight = totalChol.overall_significance_weight;
                }
                break;
              }
              default:
                break;
            }
          }

          if (typeof details.absoluteZScore === 'number' && typeof details.overall_significance_weight === 'number') {
            details.weightedAbsoluteZScore = details.absoluteZScore * (details.overall_significance_weight / 100);
          } else {
            details.weightedAbsoluteZScore = null;
          }
        });
        
        const nutritionParameterKeys = new Set(nutritionParamsRes.data ? nutritionParamsRes.data.parameters : []);

        const nutritionParameters = finalTableDataOutput.filter(p => nutritionParameterKeys.has(p.key));
        const otherParameters = finalTableDataOutput.filter(p => !nutritionParameterKeys.has(p.key));

        const groupedData = {};
        otherParameters.forEach(item => {
          const category = item.category || 'Uncategorized';
          if (!groupedData[category]) {
            groupedData[category] = [];
          }
          groupedData[category].push(item);
        });

        const categoryOrder = ['Vitals', 'Biometrics', 'Physical Exam', 'Labs', 'Imaging', 'Lifestyle', 'Uncategorized'];
        
        const tableDataInGroups = categoryOrder.reduce((acc, categoryName) => {
          if (groupedData[categoryName]) {
            const sortedParameters = groupedData[categoryName].sort((a, b) => 
              a.parameterName.localeCompare(b.parameterName)
            );
            acc.push({
              categoryName: categoryName,
              parameters: sortedParameters,
            });
          }
          return acc;
        }, []);
        
        for (const categoryName in groupedData) {
          if (!categoryOrder.includes(categoryName)) {
            const sortedParameters = groupedData[categoryName].sort((a, b) =>
              a.parameterName.localeCompare(b.parameterName)
            );
            tableDataInGroups.push({
              categoryName: categoryName,
              parameters: sortedParameters,
            });
          }
        }
        
        const sortedNutritionParameters = nutritionParameters.sort((a, b) =>
          a.parameterName.localeCompare(b.parameterName)
        );

        const finalDataStruct = [
          {
            categoryName: 'Nutrition',
            parameters: sortedNutritionParameters,
          },
          ...tableDataInGroups
        ];

        setTableData(finalDataStruct);

        // CRITICAL: Enhanced parameter validation and counting summary
        const totalParameters = finalTableDataOutput.length;
        const parametersWithData = finalTableDataOutput.filter(p => p.currentValue !== null && p.currentValue !== undefined).length;
        const parametersWithoutData = totalParameters - parametersWithData;
        const parametersWithValidZScores = finalTableDataOutput.filter(p => p.zScore !== null && p.zScore !== undefined && typeof p.zScore === 'number' && !p.error && !isNaN(p.zScore)).length;
        const parametersWithErrors = finalTableDataOutput.filter(p => p.error && p.error !== "Awaiting data").length;
        const syntheticParameters = finalTableDataOutput.filter(p => p._synthetic).length;
        const configuredParameters = totalParameters - syntheticParameters;

        // Categorize parameters by type for detailed analysis
        const parametersByCategory = finalTableDataOutput.reduce((acc, param) => {
          const category = param.category || 'Uncategorized';
          if (!acc[category]) acc[category] = [];
          acc[category].push(param);
          return acc;
        }, {});

        // Batch reporting for optimized performance
        if (invalidParameterNames.length > 0) {
          console.error(`[Parameter Validation] ❌ CRITICAL: ${invalidParameterNames.length} parameters violate parameters.md naming convention:`, invalidParameterNames.slice(0, 10));
          if (invalidParameterNames.length > 10) console.error(`[Parameter Validation] ... and ${invalidParameterNames.length - 10} more`);
        }

        if (syntheticWeightCount > 0) {
          console.log(`[Parameter Validation] 🔧 Created ${syntheticWeightCount} synthetic weight configurations for missing parameters`);
        }

        console.log(`[Parameter Validation] 📊 Data extraction summary:`, {
          mostRecent: dataExtractionStats.mostRecent,
          historicalData: dataExtractionStats.historicalData,
          results: dataExtractionStats.results,
          directValue: dataExtractionStats.directValue,
          nestedValue: dataExtractionStats.nestedValue,
          noData: dataExtractionStats.noData,
          dataStructureIssues: dataExtractionStats.dataStructureIssues
        });

        console.log(`[Parameter Validation] ✅ COMPREHENSIVE AUDIT COMPLETE`);
        console.log(`[Parameter Validation] 📊 Total parameters processed: ${totalParameters}`);
        console.log(`[Parameter Validation] 🔧 Configured parameters: ${configuredParameters}`);
        console.log(`[Parameter Validation] 🔧 Synthetic parameters (missing from config): ${syntheticParameters}`);
        console.log(`[Parameter Validation] ✅ Parameters with patient data: ${parametersWithData}`);
        console.log(`[Parameter Validation] ⚠️ Parameters without data: ${parametersWithoutData} (normal for unmeasured parameters)`);

        // TEMP DEBUG: Export params with data to window for comparison
        const paramsWithDataList = [];
        finalTableDataOutput.forEach(cat => {
          cat.parameters?.forEach(p => {
            if (p.currentValue !== null && p.currentValue !== undefined) {
              paramsWithDataList.push(p.parameter);
            }
          });
        });
        window.FRONTEND_PARAMS_WITH_DATA = paramsWithDataList.sort();
        console.log(`[TEMP DEBUG FRONTEND] Exported ${paramsWithDataList.length} params to window.FRONTEND_PARAMS_WITH_DATA`);
        console.log(`[Parameter Validation] 🎯 Parameters with valid Z-scores: ${parametersWithValidZScores}`);
        console.log(`[Parameter Validation] ❌ Parameters with errors: ${parametersWithErrors}`);
        console.log(`[Parameter Validation] 📋 Parameters by category:`, Object.entries(parametersByCategory).map(([cat, params]) => `${cat}: ${params.length}`).join(', '));
        console.log(`[Parameter Validation] ✅ Parameter naming validation: ${invalidParameterNames.length === 0 ? 'All compliant' : `${invalidParameterNames.length} violations found`}`);

        // CRITICAL VALIDATION: Check if we achieved 481 parameter processing (corrected count from parameters.md)
        const expectedParameterCount = 481;
        if (totalParameters === expectedParameterCount) {
          console.log(`[Parameter Validation] ✅ SUCCESS: All ${expectedParameterCount} parameters from parameters.md are being processed!`);
        } else {
          console.error(`[Parameter Validation] ❌ CRITICAL FAILURE: Expected ${expectedParameterCount} parameters, but only processing ${totalParameters}`);
          console.error(`[Parameter Validation] This indicates missing parameters that must be added to configuration files`);
        }

        // Critical validation: Ensure Overall Wellness Score uses ALL parameters
        console.log(`[Overall Wellness Score] 🔍 CRITICAL VALIDATION:`);
        console.log(`[Overall Wellness Score] Total parameters defined: ${totalParameters}`);
        console.log(`[Overall Wellness Score] Parameters included in calculation: ${parametersWithValidZScores}`);
        if (parametersWithValidZScores < totalParameters) {
          console.warn(`[Overall Wellness Score] ⚠️ DISCREPANCY DETECTED: Only ${parametersWithValidZScores} out of ${totalParameters} parameters are included in Overall Wellness Score calculation`);
          console.warn(`[Overall Wellness Score] This may indicate missing patient data, configuration errors, or calculation issues`);
        } else {
          console.log(`[Overall Wellness Score] ✅ All parameters properly included in calculation`);
        }

        const overallDeviationResult = calculateWeightedAverageAbsoluteZScore(finalTableDataOutput);

        // Generate comprehensive parameter count validation report (optimized mode for performance)
        logParameterCountReport(finalDataStruct, overallDeviationResult, false);
        setOverallDeviation({
          average: overallDeviationResult.averageAbsoluteZScore,
          count: overallDeviationResult.count,
          sumOfAbsoluteZScores: overallDeviationResult.sumOfWeightedZScores
        });

        if (mergedParameterWeightsData) {
          const { systemDeviations: sysDevs, lifestyleDomainDeviations: lifeDevs } =
            calculateSystemAndLifestyleDeviations(finalTableDataOutput, mergedParameterWeightsData, nutritionParameterKeys);
          setSystemDeviations(sysDevs || {});
          setLifestyleDeviations(lifeDevs || {});
          const coreComps = {};
          for (const domainKey in lifeDevs) {
            if (lifeDevs[domainKey].coreComponents) coreComps[domainKey] = lifeDevs[domainKey].coreComponents;
          }
          setCoreLifestyleComponents(coreComps);
        }

      } catch (e) {
        setError(e.message || "An error occurred while fetching or processing health data.");
        setTableData([]);
        setOverallDeviation({ average: null, count: 0, sumOfAbsoluteZScores: 0 });
        setSystemDeviations({});
        setLifestyleDeviations({});
        setCoreLifestyleComponents({});
      } finally {
        setLoading(false);
      }
    };

    fetchDataAndProcess();
  }, [patient?._id, previewData]);

  return {
    loading,
    error,
    tableData, 
    overallDeviation, 
    systemDeviations, 
    lifestyleDeviations, 
    coreLifestyleComponents, 
    patientDemographics, 
    allParamsConfig: allParamsConfigData 
  };
};

export default usePortalHealthScoring;
